import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { roadStatuses } from '../mockDataVegrExpanded';
import { mockWeatherData, getWeatherSeverity, getWeatherColor, generateWeatherAlert, isMonsoonSeason } from '../services/weatherService';
import { AlertTriangle, CheckCircle, XCircle, Navigation, Clock, Cloud, Wind, Droplets, Eye, Settings, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Smaller, more professional marker icons
const createCustomIcon = (status) => {
  const colors = {
    clear: '#4A7C59',
    damaged: '#F59E0B',
    blocked: '#DC2626'
  };
  
  const icons = {
    clear: '✓',
    damaged: '⚠',
    blocked: '✕'
  };

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${colors[status]};
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s ease;
      ">
        <span style="
          font-size: 14px;
          font-weight: bold;
          color: white;
        ">${icons[status]}</span>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};

// Smaller weather marker
const createWeatherIcon = (severity) => {
  const color = getWeatherColor(severity);
  
  return L.divIcon({
    className: 'weather-marker',
    html: `
      <div style="
        background: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="font-size: 12px;">☁</span>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const VegrMapMobile = ({ allReports = [], selectedStatus = null, selectedState = null, height = '600px' }) => {
  const [activeReport, setActiveReport] = useState(null);
  const [showWeather, setShowWeather] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showReports, setShowReports] = useState(true);
  const [weatherData, setWeatherData] = useState([]);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [legendMinimized, setLegendMinimized] = useState(true);
  
  const center = [26.0, 93.0];
  const zoom = 7;
  const monsoonActive = isMonsoonSeason();

  useEffect(() => {
    setWeatherData(mockWeatherData);
  }, []);

  const getRoadColor = (status) => {
    const statusObj = roadStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : '#6B7280';
  };

  const displayReports = useMemo(() => {
    let filtered = allReports;
    
    if (selectedState) {
      filtered = filtered.filter(r => r.state === selectedState);
    }
    
    if (selectedStatus && selectedStatus !== 'all') {
      filtered = filtered.filter(r => r.status === selectedStatus);
    }
    
    return filtered;
  }, [selectedState, selectedStatus]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'clear':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'damaged':
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      case 'blocked':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const severeWeatherCount = weatherData.filter(
    station => getWeatherSeverity(station.weather) === 'severe'
  ).length;

  const activeLayersCount = [showReports, showWeather, showTraffic].filter(Boolean).length;

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl">
      {/* Monsoon Alert - More compact */}
      {monsoonActive && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1001] bg-orange-500 text-white px-3 py-1.5 rounded-full shadow-lg text-xs font-semibold">
          <AlertTriangle className="w-3 h-3 inline mr-1" />
          Monsoon Season
        </div>
      )}

      {/* Floating Controls Button - Bottom Right */}
      <div className="absolute bottom-6 right-6 z-[1001]">
        <button
          onClick={() => setControlsOpen(!controlsOpen)}
          className="w-14 h-14 bg-[#2C4A5E] hover:bg-[#1e3544] text-white rounded-full shadow-2xl flex items-center justify-center transition-all"
        >
          {controlsOpen ? <X className="w-6 h-6" /> : <Settings className="w-6 h-6" />}
          {activeLayersCount > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full text-xs flex items-center justify-center font-bold">
              {activeLayersCount}
            </div>
          )}
        </button>
      </div>

      {/* Slide-up Controls Panel */}
      {controlsOpen && (
        <div className="absolute bottom-24 right-6 z-[1001] bg-white rounded-2xl shadow-2xl p-4 w-64 max-h-96 overflow-y-auto">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center justify-between">
            <span>Map Layers</span>
            <button onClick={() => setControlsOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </h3>
          
          <div className="space-y-2">
            <button
              onClick={() => setShowReports(!showReports)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                showReports 
                  ? 'bg-[#2C4A5E] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Navigation className="w-4 h-4" />
                <span className="text-sm font-medium">Road Reports</span>
              </div>
              <Badge className={showReports ? "bg-white/20" : "bg-gray-200 text-gray-700"}>
                {displayReports.length}
              </Badge>
            </button>

            <button
              onClick={() => setShowWeather(!showWeather)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                showWeather 
                  ? 'bg-[#2C4A5E] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4" />
                <span className="text-sm font-medium">Weather</span>
              </div>
              {severeWeatherCount > 0 && (
                <Badge className="bg-red-500 text-white">
                  {severeWeatherCount}
                </Badge>
              )}
            </button>

            <button
              onClick={() => setShowTraffic(!showTraffic)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                showTraffic 
                  ? 'bg-[#2C4A5E] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Wind className="w-4 h-4" />
                <span className="text-sm font-medium">Traffic Flow</span>
              </div>
            </button>
          </div>

          {/* Quick Stats */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-2">QUICK STATS</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Clear Roads:</span>
                <span className="font-semibold text-green-600">{displayReports.filter(r => r.status === 'clear').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Damaged:</span>
                <span className="font-semibold text-amber-600">{displayReports.filter(r => r.status === 'damaged').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Blocked:</span>
                <span className="font-semibold text-red-600">{displayReports.filter(r => r.status === 'blocked').length}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minimized Legend - Top Right */}
      {!legendMinimized && (
        <div className="absolute top-16 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 max-w-[200px]">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-gray-900 text-sm">Legend</h4>
            <button onClick={() => setLegendMinimized(true)} className="text-gray-400 hover:text-gray-600">
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
          
          {showReports && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-gray-700 mb-1">Road Status:</p>
              <div className="space-y-1">
                {roadStatuses.map((status) => {
                  const count = displayReports.filter(r => r.status === status.value).length;
                  return (
                    <div key={status.value} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: status.color }}
                        ></div>
                        <span className="text-xs text-gray-700">{status.label}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-900">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showWeather && (
            <div className="border-t border-gray-200 pt-2">
              <p className="text-xs font-semibold text-gray-700 mb-1">Weather:</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-600"></div>
                  <span className="text-xs text-gray-700">Severe</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-xs text-gray-700">Moderate</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legend Toggle Button */}
      {legendMinimized && (showReports || showWeather) && (
        <button
          onClick={() => setLegendMinimized(false)}
          className="absolute top-16 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-white"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      )}

      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height, width: '100%' }}
        className="z-0"
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {showTraffic && (
          <TileLayer
            url="https://tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=demo"
            opacity={0.6}
          />
        )}

        {showReports && displayReports.map((report) => (
          <React.Fragment key={report.id}>
            <Marker
              position={report.coordinates}
              icon={createCustomIcon(report.status)}
              eventHandlers={{
                mouseover: () => setActiveReport(report.id),
                mouseout: () => setActiveReport(null)
              }}
            >
              <Popup className="custom-popup" maxWidth={300}>
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(report.status)}
                    <h3 className="font-bold text-gray-900 text-sm">{report.location}</h3>
                  </div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">{report.roadName}</p>
                  <Badge 
                    className="mb-2 text-white text-xs"
                    style={{ backgroundColor: getRoadColor(report.status) }}
                  >
                    {report.status.toUpperCase()}
                  </Badge>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {report.state}
                  </Badge>
                  <p className="text-xs text-gray-700 mb-2 mt-2">{report.description}</p>
                  {report.photo && (
                    <img 
                      src={report.photo} 
                      alt={report.location}
                      className="w-full h-24 object-cover rounded-lg mb-2"
                    />
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(report.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    <strong>By:</strong> {report.reportedBy}
                    {report.verified && <span className="ml-2 text-green-600">✓</span>}
                  </div>
                </div>
              </Popup>
            </Marker>

            {activeReport === report.id && (
              <Circle
                center={report.coordinates}
                radius={800}
                pathOptions={{
                  fillColor: getRoadColor(report.status),
                  fillOpacity: 0.15,
                  color: getRoadColor(report.status),
                  weight: 1
                }}
              />
            )}
          </React.Fragment>
        ))}

        {showWeather && weatherData.map((station, idx) => {
          const severity = getWeatherSeverity(station.weather);
          const alerts = generateWeatherAlert(station.weather, station.name);
          
          return (
            <React.Fragment key={idx}>
              <Marker
                position={station.coordinates}
                icon={createWeatherIcon(severity)}
              >
                <Popup maxWidth={280}>
                  <div className="p-2">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                      <Cloud className="w-4 h-4" />
                      {station.name}, {station.state}
                    </h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold">{station.weather.temperature}°C</span>
                        <span className="text-xs text-gray-600 capitalize">{station.weather.description}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1">
                          <Droplets className="w-3 h-3 text-blue-500" />
                          <span>{station.weather.humidity}%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Wind className="w-3 h-3 text-gray-500" />
                          <span>{Math.round(station.weather.windSpeed * 3.6)}km/h</span>
                        </div>
                      </div>

                      {alerts && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                          <p className="text-xs font-semibold text-red-700">Alerts:</p>
                          {alerts.map((alert, i) => (
                            <p key={i} className="text-xs text-red-600">{alert}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>

              {severity !== 'normal' && (
                <Circle
                  center={station.coordinates}
                  radius={4000}
                  pathOptions={{
                    fillColor: getWeatherColor(severity),
                    fillOpacity: 0.08,
                    color: getWeatherColor(severity),
                    weight: 1,
                    dashArray: '3, 3'
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Live indicator - smaller */}
      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-full shadow-lg px-3 py-1.5 z-[1000]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-semibold text-gray-900">Live</span>
        </div>
      </div>
    </div>
  );
};

export default VegrMapMobile;